<template>
  <div
    class="hcl-form-input-checkbox form-check"
    :style="computedStyleCheckbox"
  >
    <input
      ref="refCheckbox"
      type="checkbox"
      class="hcl-form-input-checkbox-input form-check-input"
      @click.stop="onClicked"
      :class="{ 'position-static': pstate.label === '' }"
      :id="pstate.id"
      :name="pstate.name"
      :value="cstate.v"
      v-model="cstate.s"
      :disabled="pstate.disabled"
      :indeterminate="pstate.indeterminate"
      :aria-label="pstate.id"
    />
    <slot>
      <label
        v-if="pstate.label"
        :for="pstate.id"
        class="hcl-form-input-checkbox-label form-check-label"
      >
        {{ pstate.label }}
      </label>
    </slot>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch } from 'vue'

interface Props {
  /*
   * The id assigned to the input field
   */
  id: string | undefined

  /*
   * The name of the input field in a checkbox (Useful for form submission)
   */
  name: string | undefined

  /*
   * The value to be used in the checkbox input. This is the value that will be returned on form submission.
   */
  value: string | number | undefined

  /*
   * Whether the checkbox is checked by default or not
   */
  checked: string

  /*
   * Whether the checkbox is indeterminate  by default or not
   */
  indeterminate?: string

  /*
   * Whether the checkbox is disabled  by default or not
   */
  disabled?: string

  /*
   * A label for the checkbox
   */
  label?: string

  /**
   * Width of the Checkboxes in %
   */
  width?: number | undefined
}

const props = withDefaults(defineProps<Props>(), {
  id: undefined,
  name: undefined,
  value: undefined,
  checked: 'false',
  indeterminate: 'false',
  disabled: 'false',
  label: undefined,
  width: undefined
})

const pstate = reactive({
  id: props.id,
  name: props.name,
  value: props.value,
  checked: props.checked === 'false' ? false : true,
  indeterminate: props.indeterminate === 'false' ? false : true,
  disabled: props.disabled === 'false' ? false : true,
  label: props.label
})

const cstate = reactive({
  s: pstate.checked,
  v: pstate.checked ? pstate.value : null
})

const refCheckbox = ref()

const emit = defineEmits([
  'hcl-form-input-checkbox-changed'
])

const onClicked = () => {
  emit('hcl-form-input-checkbox-changed', {
    id: props.id,
    name: props.name,
    value: props.value,
    checked: cstate.s === false
  })
}

watch(props, (n) => {
  pstate.checked = n.checked === 'true' ? true : false
  cstate.s = pstate.checked
  cstate.v = pstate.checked ? pstate.value : null
})

const computedStyleCheckbox = computed(() => {
  if (props.width === undefined) return ''

  return { width: `${props.width}%` }
})
</script>
